import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Tag, Check, Clock } from "lucide-react";
import { useSiteData } from "../../context/SiteData";
import { useBooking } from "../../context/BookingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SpecialOffers = () => {
  const { fmtPrice, locale } = useSiteData();
  const { openBooking } = useBooking();
  const rtl = locale === "ar";
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchOffers = useCallback(() => {
    axios.get(`${API}/offers`).then((r) => setOffers(r.data.offers || [])).catch(() => setOffers([]));
  }, []);

  useEffect(() => {
    fetchOffers();
    const onOpen = () => { fetchOffers(); setOpen(true); };
    window.addEventListener("voyage:open-offers", onOpen);
    return () => window.removeEventListener("voyage:open-offers", onOpen);
  }, [fetchOffers]);

  const bookOffer = (offer) => { setOpen(false); openBooking({ mode: "tour", offer }); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[190] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#0b132b]/55 backdrop-blur-sm overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} dir={rtl ? "rtl" : "ltr"} data-testid="offers-modal-overlay">
          <motion.div onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="w-full sm:max-w-2xl max-h-[92dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl" data-testid="offers-modal">
            <div className="sticky top-0 z-10 flex items-center justify-between bg-white/95 backdrop-blur px-6 sm:px-8 py-5 border-b border-slate-100">
              <h3 className="font-display font-bold text-xl text-[#0b132b] flex items-center gap-2"><Tag className="w-5 h-5 text-[var(--brand)]" /> Special Offers</h3>
              <button data-testid="offers-close-button" onClick={() => setOpen(false)} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 sm:p-8 space-y-5">
              {offers.length === 0 && <p className="text-center text-sm text-slate-400 py-10" data-testid="offers-empty">No active offers right now — check back soon!</p>}
              {offers.map((o) => (
                <div key={o.id} data-testid={`offer-card-${o.id}`} className="rounded-2xl border border-slate-200 overflow-hidden flex flex-col sm:flex-row">
                  {o.image && <img src={o.image} alt={o.title_en} className="w-full sm:w-44 h-40 sm:h-auto object-cover" />}
                  <div className="p-5 flex-1">
                    <h4 className="font-display font-bold text-lg text-[#0b132b]">{rtl && o.title_ar ? o.title_ar : o.title_en}</h4>
                    {(o.description_en || o.description_ar) && <p className="mt-1 text-sm text-slate-600">{rtl && o.description_ar ? o.description_ar : o.description_en}</p>}
                    {(o.inclusions || []).length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {o.inclusions.map((inc, i) => <li key={i} className="flex items-center gap-2 text-xs text-slate-600"><Check className="w-3.5 h-3.5 text-emerald-500" /> {inc}</li>)}
                      </ul>
                    )}
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-baseline gap-2">
                        {o.original_price > 0 && <span className="text-sm text-slate-400 line-through">{fmtPrice(o.original_price)}</span>}
                        <span className="text-2xl font-display font-extrabold text-[var(--brand)]" data-testid={`offer-price-${o.id}`}>{fmtPrice(o.offer_price)}</span>
                      </div>
                      <button data-testid={`offer-book-${o.id}`} onClick={() => bookOffer(o)}
                        className="rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 hover:bg-[var(--brand-dark)] transition">Book This Offer</button>
                    </div>
                    {(o.valid_to || o.conditions_en) && (
                      <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {o.valid_to ? `Valid until ${o.valid_to}. ` : ""}{rtl && o.conditions_ar ? o.conditions_ar : o.conditions_en}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
